import { type LiveKitSessionUsage, parseLiveKitSessionUsage } from "./livekit-session-usage";

export type LiveKitAgentTokenSource = "prewarm" | "fresh";

export type LiveKitAgentTokenCacheEntry = {
  token: string;
  url: string;
  roomName: string;
  participantName?: string;
  shutdownCapability?: string;
  expiresAt: number;
  languageCode: string;
  sessionMode: string;
  learningMode?: string;
  tutorWhiteboardEnabled?: boolean;
  fromLanguageCode?: string;
  fromLanguageName?: string;
  voice?: string;
  avatarId?: string;
  teachingStyle?: string;
  nickname?: string;
  customInstructions?: string;
  noAvatar?: boolean;
  model?: string;
  tutorId?: string;
  tutorPrompt?: string;
  tutorCurriculumItemId?: string | null;
  journeyChapterId?: string | null;
  podcastDiscussionId?: string | null;
  sessionVocabularyKey?: string;
  tokenSource?: LiveKitAgentTokenSource;
  usage: LiveKitSessionUsage;
  version?: number;
};

export type LiveKitConnectionDetailsTokenPayload = {
  participantToken: string;
  serverUrl: string;
  roomName: string;
  participantName?: string;
  shutdownCapability?: string;
  tokenSource?: LiveKitAgentTokenSource;
  usage: LiveKitSessionUsage;
};

export function toLiveKitConnectionDetailsTokenPayload(
  value: unknown
): LiveKitConnectionDetailsTokenPayload {
  const payload = (value && typeof value === "object" ? value : {}) as {
    participantToken?: unknown;
    serverUrl?: unknown;
    roomName?: unknown;
    participantName?: unknown;
    shutdownCapability?: unknown;
    tokenSource?: unknown;
    usage?: unknown;
  };
  const usage = parseLiveKitSessionUsage(payload.usage);
  if (!usage) {
    throw new Error("Connection details did not include a valid server-authored usage policy");
  }

  return {
    participantToken: String(payload.participantToken || ""),
    serverUrl: String(payload.serverUrl || ""),
    roomName: String(payload.roomName || ""),
    participantName:
      typeof payload.participantName === "string" && payload.participantName
        ? payload.participantName
        : undefined,
    shutdownCapability:
      typeof payload.shutdownCapability === "string" && payload.shutdownCapability
        ? payload.shutdownCapability
        : undefined,
    tokenSource:
      payload.tokenSource === "prewarm" || payload.tokenSource === "fresh"
        ? payload.tokenSource
        : undefined,
    usage,
  };
}

export type BuildLiveKitAgentTokenCacheEntryInput = {
  connectionDetails: LiveKitConnectionDetailsTokenPayload;
  languageCode: string;
  sessionMode: string;
  learningMode?: string;
  tutorWhiteboardEnabled?: boolean;
  fromLanguageCode?: string;
  fromLanguageName?: string;
  voice?: string;
  avatarId?: string;
  teachingStyle?: string;
  nickname?: string;
  customInstructions?: string;
  noAvatar?: boolean;
  model?: string;
  tutorId?: string;
  tutorPrompt?: string;
  tutorCurriculumItemId?: string | null;
  journeyChapterId?: string | null;
  podcastDiscussionId?: string | null;
  sessionVocabularyKey?: string;
  nowMs: number;
  cacheVersion: number;
};

export type IsLiveKitAgentTokenCacheEntryValidInput = {
  cachedToken: LiveKitAgentTokenCacheEntry | null;
  cacheVersion: number;
  languageCode: string;
  sessionMode: string;
  learningMode?: string;
  tutorWhiteboardEnabled?: boolean;
  fromLanguageCode?: string;
  fromLanguageName?: string;
  voice?: string;
  avatarId?: string;
  teachingStyle?: string;
  nickname?: string;
  customInstructions?: string;
  noAvatar?: boolean;
  model?: string;
  tutorId?: string;
  tutorPrompt?: string;
  tutorCurriculumItemId?: string | null;
  journeyChapterId?: string | null;
  podcastDiscussionId?: string | null;
  sessionVocabularyKey?: string;
  nowMs: number;
  isRoomConnected: boolean;
};

const TOKEN_CACHE_TTL_MS = 10 * 60 * 1000;
const TOKEN_CACHE_MIN_REMAINING_MS = 2 * 60 * 1000;

export function buildLiveKitAgentTokenCacheEntry({
  connectionDetails,
  languageCode,
  sessionMode,
  learningMode,
  tutorWhiteboardEnabled,
  fromLanguageCode,
  fromLanguageName,
  voice,
  avatarId,
  teachingStyle,
  nickname,
  customInstructions,
  noAvatar,
  model,
  tutorId,
  tutorPrompt,
  tutorCurriculumItemId,
  journeyChapterId,
  podcastDiscussionId,
  sessionVocabularyKey,
  nowMs,
  cacheVersion,
}: BuildLiveKitAgentTokenCacheEntryInput): LiveKitAgentTokenCacheEntry {
  return {
    token: connectionDetails.participantToken,
    url: connectionDetails.serverUrl,
    roomName: connectionDetails.roomName,
    participantName: connectionDetails.participantName,
    shutdownCapability: connectionDetails.shutdownCapability,
    languageCode,
    sessionMode,
    ...(learningMode ? { learningMode } : {}),
    tutorWhiteboardEnabled,
    fromLanguageCode,
    fromLanguageName,
    voice,
    avatarId,
    teachingStyle,
    nickname,
    customInstructions,
    noAvatar,
    model,
    tutorId,
    tutorPrompt,
    tutorCurriculumItemId,
    journeyChapterId,
    podcastDiscussionId,
    sessionVocabularyKey,
    tokenSource: connectionDetails.tokenSource,
    usage: connectionDetails.usage,
    expiresAt: nowMs + TOKEN_CACHE_TTL_MS,
    version: cacheVersion,
  };
}

export function isLiveKitAgentTokenCacheEntryValid({
  cachedToken,
  cacheVersion,
  languageCode,
  sessionMode,
  learningMode,
  tutorWhiteboardEnabled,
  fromLanguageCode,
  fromLanguageName,
  voice,
  avatarId,
  teachingStyle,
  nickname,
  customInstructions,
  noAvatar,
  model,
  tutorId,
  tutorPrompt,
  tutorCurriculumItemId,
  journeyChapterId,
  podcastDiscussionId,
  sessionVocabularyKey,
  nowMs,
  isRoomConnected,
}: IsLiveKitAgentTokenCacheEntryValidInput): boolean {
  return Boolean(
    cachedToken &&
    cachedToken.version === cacheVersion &&
    cachedToken.roomName &&
    parseLiveKitSessionUsage(cachedToken.usage) &&
    cachedToken.languageCode === languageCode &&
    cachedToken.sessionMode === sessionMode &&
    cachedToken.learningMode === learningMode &&
    cachedToken.tutorWhiteboardEnabled === tutorWhiteboardEnabled &&
    cachedToken.fromLanguageCode === fromLanguageCode &&
    cachedToken.fromLanguageName === fromLanguageName &&
    cachedToken.voice === voice &&
    cachedToken.avatarId === avatarId &&
    cachedToken.teachingStyle === teachingStyle &&
    cachedToken.nickname === nickname &&
    cachedToken.customInstructions === customInstructions &&
    cachedToken.noAvatar === noAvatar &&
    cachedToken.model === model &&
    cachedToken.tutorId === tutorId &&
    cachedToken.tutorPrompt === tutorPrompt &&
    cachedToken.tutorCurriculumItemId === tutorCurriculumItemId &&
    cachedToken.journeyChapterId === journeyChapterId &&
    cachedToken.podcastDiscussionId === podcastDiscussionId &&
    cachedToken.sessionVocabularyKey === sessionVocabularyKey &&
    cachedToken.expiresAt > nowMs + TOKEN_CACHE_MIN_REMAINING_MS &&
    !isRoomConnected
  );
}
